import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Moon, Star } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const Landing = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-[#F8F9FA] relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/adfa46bb-e966-4f22-bd70-893aead51158/images/e7685f890130abde423641a53b0d62342f8ecd0f6aeb9e930ed97fff4466e3f2.png" 
          alt="Mystical background"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/50 to-[#050505]"></div>
      </div>

      {/* Stars Texture Overlay */}
      <div className="absolute inset-0 opacity-30">
        <img 
          src="https://images.unsplash.com/photo-1622793139910-e261be0b30a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbmlnaHQlMjBza3klMjBzdGFycyUyMHRleHR1cmV8ZW58MHx8fHwxNzc5NzI4NDczfDA&ixlib=rb-4.1.0&q=85"
          alt="Stars"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 glass-effect border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-2xl font-heading font-light tracking-tight">Seatask Arcane AI</span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <button
              onClick={() => navigate('/auth')}
              data-testid="nav-signin-button"
              className="px-6 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-md hover:bg-[#D4AF37] hover:text-[#050505] transition-all duration-300 font-body text-sm"
            >
              Sign In
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="dark-zone relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-32">
        <div className="max-w-4xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0F0F11]/60 backdrop-blur-xl border border-white/10 mb-8 fade-in">
            <Moon className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-xs tracking-[0.2em] uppercase text-zinc-400">AI-Powered Mystical Guidance</span>
          </div>

          {/* Main Heading */}
          <h1 className="text-6xl sm:text-7xl lg:text-8xl font-heading font-light tracking-tighter leading-none mb-8 fade-in" style={{animationDelay: '0.1s'}}>
            Discover Your
            <br />
            <span className="text-[#D4AF37]">Destiny</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl text-zinc-300 leading-relaxed mb-12 max-w-2xl fade-in" style={{animationDelay: '0.2s'}}>
            Unlock ancient wisdom through the mystical art of tarot. Our AI-powered readings provide personalized insights to guide your journey.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 fade-in" style={{animationDelay: '0.3s'}}>
            <button
              onClick={() => navigate('/auth')}
              data-testid="get-started-button"
              className="px-8 py-4 bg-[#D4AF37] text-[#050505] rounded-md hover:bg-[#F3E5AB] transition-all duration-300 font-body font-medium text-base flex items-center gap-2 shadow-lg shadow-[#D4AF37]/20"
            >
              <Sparkles className="w-5 h-5" strokeWidth={1.5} />
              Get Started
            </button>
            <button
              onClick={() => {
                document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
              }}
              data-testid="learn-more-button"
              className="px-8 py-4 border border-[#D4AF37] text-[#D4AF37] rounded-md hover:bg-[#D4AF37] hover:text-[#050505] transition-all duration-300 font-body font-medium text-base"
            >
              Learn More
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="dark-zone relative z-10 bg-gradient-to-b from-transparent to-[#050505] py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-heading font-medium tracking-tight mb-4">Mystical Features</h2>
            <p className="text-zinc-400 text-base">Explore the power of AI-enhanced tarot readings</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="glass-effect rounded-lg p-8 card-hover" data-testid="feature-daily-reading">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Moon className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-heading font-medium mb-3">Daily Guidance</h3>
              <p className="text-zinc-400 leading-relaxed">Receive personalized daily tarot readings to guide your path and illuminate your day.</p>
            </div>

            {/* Feature 2 */}
            <div className="glass-effect rounded-lg p-8 card-hover" data-testid="feature-multiple-spreads">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Sparkles className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-heading font-medium mb-3">Multiple Spreads</h3>
              <p className="text-zinc-400 leading-relaxed">Choose from single card, three-card, five-card Cross, and the comprehensive Celtic Cross.</p>
            </div>

            {/* Feature 3 */}
            <div className="glass-effect rounded-lg p-8 card-hover" data-testid="feature-ai-interpretations">
              <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                <Star className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-heading font-medium mb-3">AI Interpretations</h3>
              <p className="text-zinc-400 leading-relaxed">Advanced AI generates deeply personalized interpretations tailored to your unique situation.</p>
            </div>
          </div>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default Landing;
